// src/pages/attendance/hooks/useLocationState.js
import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
  getAllowedArea, calculateAccurateDistance, locationTracker,
  acquireLocation, getGeolocationPermission, LocationError,
} from "../utils/locationService";

const accuracyLevelOf = (accuracy) =>
  accuracy <= 10 ? "excellent" : accuracy <= 20 ? "good" : accuracy <= 50 ? "acceptable" : "poor";

const asLocationError = (err, fallbackCode) =>
  err instanceof LocationError ? err : new LocationError(fallbackCode, err?.message);

// `areaError` (the work location can't be resolved) and `gpsError` (the
// device position can't be read) are kept apart so the page can tell the user
// which one is wrong; `error` is whichever is set, for older readers.
//
// `isRemote` (Remote Punch): the device position is still required, but no
// work location is — so live tracking runs without one too.
export const useLocationState = ({ hasApprovedWFHToday, isCheckingWFH, infinityCode, BASE_URL, isRemote = false }) => {
  const [locationState, setLocationState] = useState({
    isLoadingArea: true, isLoadingUserLocation: true,
    allowedArea: null, userLocation: null,
    areaError: null, gpsError: null,
    accuracy: null, accuracyLevel: "unknown",
    isTracking: false, lastUpdate: null,
  });
  const [areaAttempt, setAreaAttempt] = useState(0);
  const [gpsAttempt, setGpsAttempt] = useState(0);
  const skipLocation = isCheckingWFH || hasApprovedWFHToday;

  const gpsErrorRef = useRef(null);
  gpsErrorRef.current = locationState.gpsError;

  // fetch work area
  useEffect(() => {
    if (skipLocation) {
      if (hasApprovedWFHToday) setLocationState((p) => ({ ...p, isLoadingArea: false, isLoadingUserLocation: false }));
      return;
    }
    if (!infinityCode || !BASE_URL) {
      setLocationState((p) => ({ ...p, isLoadingArea: false, allowedArea: null, areaError: new LocationError("MISSING_EMPLOYEE_CODE") }));
      return;
    }
    let mounted = true;
    setLocationState((p) => ({ ...p, isLoadingArea: true, areaError: null }));
    (async () => {
      try {
        const area = await getAllowedArea(infinityCode, BASE_URL);
        if (mounted) setLocationState((p) => ({ ...p, isLoadingArea: false, allowedArea: area, areaError: null }));
      } catch (err) {
        if (mounted) setLocationState((p) => ({ ...p, isLoadingArea: false, allowedArea: null, areaError: asLocationError(err, "AREA_LOAD_FAILED") }));
      }
    })();
    return () => { mounted = false; };
  }, [infinityCode, BASE_URL, hasApprovedWFHToday, skipLocation, areaAttempt]);

  // get user GPS
  useEffect(() => {
    if (skipLocation) return;
    let mounted = true;
    setLocationState((p) => ({ ...p, isLoadingUserLocation: true, gpsError: null }));
    (async () => {
      try {
        const loc = await acquireLocation(3);
        if (!mounted) return;
        setLocationState((p) => ({ ...p, isLoadingUserLocation: false, userLocation: loc, accuracy: loc.accuracy, accuracyLevel: accuracyLevelOf(loc.accuracy), lastUpdate: new Date(), gpsError: null }));
      } catch (err) {
        if (!mounted) return;
        setLocationState((p) => ({ ...p, isLoadingUserLocation: false, gpsError: asLocationError(err, "POSITION_UNAVAILABLE") }));
      }
    })();
    return () => { mounted = false; };
  }, [skipLocation, gpsAttempt]);

  // Follow the browser's location permission: re-read the position as soon as
  // the user allows it in site settings, and lock check-in if they revoke it.
  useEffect(() => {
    if (skipLocation) return;
    let status = null;
    let cancelled = false;
    const onChange = () => {
      if (status.state === "granted") {
        if (gpsErrorRef.current) setGpsAttempt((n) => n + 1);
      } else if (status.state === "denied") {
        setLocationState((p) => ({ ...p, isLoadingUserLocation: false, userLocation: null, gpsError: new LocationError("PERMISSION_DENIED") }));
      }
    };
    getGeolocationPermission().then((s) => {
      if (!s || cancelled) return;
      status = s;
      status.addEventListener?.("change", onChange);
    });
    return () => {
      cancelled = true;
      status?.removeEventListener?.("change", onChange);
    };
  }, [skipLocation]);

  // continuous tracking — keyed on presence, not on every position update,
  // so the watch isn't torn down and restarted each time it reports.
  const hasUserLocation = !!locationState.userLocation;
  const hasAllowedArea = !!locationState.allowedArea;
  useEffect(() => {
    if (!hasUserLocation || (!hasAllowedArea && !isRemote)) return;
    locationTracker.startContinuousTracking(
      ({ location, accuracyLevel }) => {
        setLocationState((p) => ({ ...p, userLocation: location, accuracy: location.accuracy, accuracyLevel, lastUpdate: new Date(), isTracking: true, gpsError: null }));
      },
      (err) => {
        // Transient misses are fine (the last fix stays); a revoked
        // permission means the position can no longer be trusted.
        if (err.code === "PERMISSION_DENIED") {
          setLocationState((p) => ({ ...p, userLocation: null, isTracking: false, gpsError: err }));
        }
      }
    );
    return () => locationTracker.stopContinuousTracking();
  }, [hasUserLocation, hasAllowedArea, isRemote]);

  // Evaluate the user against every allowed area. In-zone if inside ANY of them.
  // `distance` reports the distance to the nearest area (for display).
  const zoneEval = useMemo(() => {
    const { userLocation, allowedArea } = locationState;
    const areas = allowedArea?.areas?.length
      ? allowedArea.areas
      : allowedArea
        ? [allowedArea]
        : [];
    if (!userLocation || areas.length === 0) return { distance: null, inAnyZone: false };

    const buffer = Math.min(userLocation.accuracy || 0, 50);
    let nearest = null;
    let inAnyZone = false;
    for (const area of areas) {
      const d = calculateAccurateDistance(userLocation.lat, userLocation.lng, area.lat, area.lng);
      if (d === null) continue;
      if (nearest === null || d < nearest) nearest = d;
      if (d <= area.radius + buffer) inAnyZone = true;
    }
    return { distance: nearest, inAnyZone };
  }, [locationState.userLocation, locationState.allowedArea]);

  const distance = zoneEval.distance;

  const isOutOfZone = useMemo(() => {
    if (hasApprovedWFHToday) return false;
    if (locationState.isLoadingArea || locationState.isLoadingUserLocation) return true;
    if (!locationState.allowedArea || zoneEval.distance === null) return true;
    return !zoneEval.inAnyZone;
  }, [zoneEval, locationState, hasApprovedWFHToday]);

  const refreshLocation = useCallback(async () => {
    setLocationState((p) => ({ ...p, isLoadingUserLocation: true }));
    try {
      const loc = await acquireLocation(2);
      setLocationState((p) => ({ ...p, isLoadingUserLocation: false, userLocation: loc, accuracy: loc.accuracy, accuracyLevel: accuracyLevelOf(loc.accuracy), lastUpdate: new Date(), gpsError: null }));
    } catch (err) {
      const gpsError = asLocationError(err, "POSITION_UNAVAILABLE");
      setLocationState((p) => ({
        ...p,
        isLoadingUserLocation: false,
        gpsError,
        // Keep the last fix for a transient miss; drop it once access is revoked.
        userLocation: gpsError.code === "PERMISSION_DENIED" ? null : p.userLocation,
      }));
    }
  }, []);

  // Re-run whichever lookup failed (or both).
  const retryLocation = useCallback(() => {
    if (locationState.areaError) setAreaAttempt((n) => n + 1);
    if (locationState.gpsError || !locationState.userLocation) setGpsAttempt((n) => n + 1);
  }, [locationState.areaError, locationState.gpsError, locationState.userLocation]);

  const state = useMemo(
    () => ({ ...locationState, error: locationState.areaError || locationState.gpsError }),
    [locationState]
  );

  return { locationState: state, distance, isOutOfZone, refreshLocation, retryLocation };
};
